import React from "react";
import { Launcher, Proactive } from "@tomis-tech/chat-ui";

import { getChatApi } from "@/chatApi";
import { getChatDomIds } from "@/namespace";
import { useChatWidget, useFormOnly, useToast } from "@/hooks";
import { ChatWidgetConfiguration, FormInstance } from "@/types";
import {
  Container,
  LauncherContainer,
  ChatContainer,
} from "../ChatWidget/styled";
import { FormOnlyOpen } from "./FormOnlyOpen";

export interface FormOnlyWidgetProps {
  /** Callback function to run after mount and the chat API is initialized */
  onReady: () => any;
  /** Chat widget configuration stored in database */
  config: ChatWidgetConfiguration;
  /** If `true` load widget inside a <div> on the page */
  embed: boolean;
  /** If `true`, mark conversations as test_mode in database. If true, prevents product analytics */
  test_mode?: boolean;
  /** The required form key that must be displayed */
  requiredFormKey: string;
  /** Initial conversation-level forms from load */
  forms: FormInstance[];
}

/**
 * Form-only widget component.
 *
 * This component is used when the backend returns a `required_form` in the load response,
 * indicating that the widget should operate in form-only mode (no chat functionality).
 *
 * Key differences from ChatWidget:
 * - Uses useFormOnly hook instead of useRuntime
 * - Does not support chat/message functionality
 * - Form submission goes through dedicated /forms/submit endpoint
 * - Displays FormOnlyOpen instead of ChatOpen
 *
 * Still shares with ChatWidget:
 * - Uses useChatWidget for open/close, proactive messages, positioning
 * - Supports both embed and floating modes
 * - Same launcher and proactive message behavior
 */
export const FormOnlyWidget: React.FC<FormOnlyWidgetProps> = ({
  onReady,
  config,
  embed = false,
  test_mode = false,
  requiredFormKey,
  forms: initialForms = [],
}) => {
  const {
    isOpen,
    isHidden,
    proactiveMessages,
    closeWidget,
    handleOpenAndLaunch,
    launcherStyle,
    chatWindowStyle,
  } = useChatWidget({ config, embed, onReady });

  // Initialize toast for error/success notifications
  const { showToast } = useToast();

  // Initialize form-only state management
  const { activeForm, isSubmitting, updateFormValues, submitForm } =
    useFormOnly({
      siteId: getChatApi().chat.id || "",
      requiredFormKey,
      initialForms,
      onError: (message) => showToast(message, { variant: "error" }),
      onSuccess: (message) =>
        showToast(message, { variant: "success", duration: 10000 }),
    });

  const chat = getChatApi().chat;
  chat.test_mode = test_mode;
  // Form-only mode doesn't use feedback
  chat.feedback = "disabled";

  /**
   * Handle form close action.
   *
   * In form-only mode:
   * - If embedded, form cannot be closed (dismissible=false)
   * - If floating, closing the form minimizes the widget
   */
  const handleFormClose = React.useCallback(() => {
    if (!embed) {
      closeWidget();
    }
    // In embed mode, form cannot be closed - this is intentional
  }, [embed, closeWidget]);

  // Form is not dismissible in embed mode for form-only widgets
  // User must complete the form or navigate away from the page
  const formDismissible = !embed;

  // Embed mode - render form directly
  if (embed) {
    return (
      <FormOnlyOpen
        config={{
          name: config.name,
          image: config.image,
        }}
        embed={true}
        activeForm={activeForm}
        isSubmitting={isSubmitting}
        updateFormValues={updateFormValues}
        submitForm={submitForm}
        onClose={handleFormClose}
        dismissible={false}
      />
    );
  }

  const ids = getChatDomIds();

  // Floating mode - render with launcher and container
  return (
    <Container
      id={ids.host}
      $withChat={isOpen}
      $isHidden={isHidden}
      $zIndex={config.zIndex}
    >
      <LauncherContainer id={ids.closed} style={launcherStyle}>
        <Proactive
          side={config.position}
          messages={proactiveMessages}
          onMessageClick={handleOpenAndLaunch}
        />
        <Launcher
          onClick={handleOpenAndLaunch}
          image={config.launcher}
          size={config.size}
        />
      </LauncherContainer>
      <ChatContainer id={ids.open} style={chatWindowStyle}>
        <FormOnlyOpen
          config={{
            name: config.name,
            image: config.image,
          }}
          embed={false}
          activeForm={activeForm}
          isSubmitting={isSubmitting}
          updateFormValues={updateFormValues}
          submitForm={submitForm}
          onClose={handleFormClose}
          dismissible={formDismissible}
        />
      </ChatContainer>
    </Container>
  );
};
